import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    FileText,
    Cpu,
    CheckCircle,
    GitCompareArrows,
    FileSpreadsheet,
    Inbox,
    Building2,
    Lock,
    ShieldCheck,
    Globe,
    ArrowRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { authAPI } from '../../lib/api';

// ── Public landing / home page (marketing only). No auth beyond the public contact endpoint. ──
// "Book a demo" posts a real enquiry to /api/contact/enquiry, which stores it and notifies over
// the real SMTP path. The address below is only the fallback shown in the footer and in the
// failure state, so a visitor always has a way through if the form itself cannot send.
// Must stay in step with CONTACT_NOTIFY_EMAIL in backend/.env.
const DEMO_EMAIL = 'contact@accountrix.dk';

// ── Contact form ────────────────────────────────────────────────────────────────────────
// Posts to the public /api/contact/enquiry endpoint. Four states: idle, sending, sent, and two
// distinct failure states. "sent" is shown ONLY when the server confirms the notification went
// out; if the enquiry was stored but the email failed, the visitor is told exactly that and
// given the direct address instead of a false "sent".
function ContactForm({ t }) {
    const [form, setForm] = useState({ name: '', company: '', cvr: '', email: '', message: '', website: '' });
    const [state, setState] = useState('idle');   // idle | sending | sent | stored_not_sent | error
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.company.trim() || !form.email.trim() || !form.message.trim()) {
            setState('required');
            return;
        }
        setState('sending');
        try {
            const r = await authAPI.submitContactEnquiry(form);
            // Truthful: only claim "sent" when the server actually sent the notification.
            setState(r.data?.email_sent ? 'sent' : 'stored_not_sent');
        } catch (err) {
            setState('error');
        }
    };

    if (state === 'sent') {
        return (
            <div className="rounded-2xl border border-teal-200 bg-teal-50 p-6 text-left" data-testid="contact-sent">
                <p className="font-semibold text-[#0F766E]">{t.fOkTitle}</p>
                <p className="mt-1 text-sm text-slate-600">{t.fOkBody}</p>
            </div>
        );
    }
    if (state === 'stored_not_sent') {
        return (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-left" data-testid="contact-stored-not-sent">
                <p className="font-semibold text-amber-700">{t.fPartialTitle}</p>
                <p className="mt-1 text-sm text-slate-600">
                    {t.fPartialBody}{' '}
                    <a href={`mailto:${DEMO_EMAIL}`} className="underline">{DEMO_EMAIL}</a>.
                </p>
            </div>
        );
    }

    const input = 'w-full h-11 rounded-lg border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F766E]/30';
    return (
        <form onSubmit={submit} className="text-left space-y-3" data-testid="contact-form">
            {/* Honeypot: hidden from people, irresistible to bots. Never shown, never focusable. */}
            <input type="text" name="website" value={form.website} onChange={set('website')}
                   tabIndex={-1} autoComplete="off" aria-hidden="true"
                   style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />
            <div className="grid gap-3 sm:grid-cols-2">
                <input className={input} placeholder={t.fName} value={form.name} onChange={set('name')} data-testid="contact-name" />
                <input className={input} placeholder={t.fCompany} value={form.company} onChange={set('company')} data-testid="contact-company" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
                <input className={input} placeholder={t.fCvr} value={form.cvr} onChange={set('cvr')} data-testid="contact-cvr" />
                <input className={input} type="email" placeholder={t.fEmail} value={form.email} onChange={set('email')} data-testid="contact-email" />
            </div>
            <textarea className={`${input} h-28 py-2`} placeholder={t.fMessage} value={form.message}
                      onChange={set('message')} data-testid="contact-message" />
            {state === 'required' && <p className="text-sm text-amber-700" data-testid="contact-required">{t.fRequired}</p>}
            {state === 'error' && (
                <p className="text-sm text-red-600" data-testid="contact-error">
                    <span className="font-semibold">{t.fErrTitle}</span>{' '}{t.fErrBody}{' '}
                    <a href={`mailto:${DEMO_EMAIL}`} className="underline">{DEMO_EMAIL}</a>.
                </p>
            )}
            <Button type="submit" size="lg" disabled={state === 'sending'}
                    className="bg-[#0F766E] hover:bg-[#0c5d56] text-white px-8" data-testid="contact-submit">
                {state === 'sending' ? t.fSending : t.fSend}
            </Button>
        </form>
    );
}

// Bilingual copy. Default = DA (Danish market). Toggle in the header.
const STR = {
    da: {
        nav: { how: 'Sådan virker det', inbox: 'Klientindbakke', pricing: 'Pris', security: 'Sikkerhed' },
        login: 'Log ind',
        eyebrow: 'AI-forberedt bogføring til danske revisorer og bogholdere — Uniconta og e-conomic',
        headline: 'Gør dage med bilagsindtastning til timers gennemgang.',
        subhead: 'Accountrix henter dine klienters bilag direkte fra indbakken i deres eget regnskabssystem, koder dem efter netop den klients kontoplan og de rigtige danske momskoder, matcher dem mod banken og bogfører dem tilbage i e-conomic eller Uniconta — så dit team gennemgår i stedet for at taste. I har altid kontrollen; intet bogføres uden jeres godkendelse.',
        btnDemo: 'Book en demo',
        btnHow: 'Se hvordan det virker',
        trust: ['Virker med Uniconta og e-conomic', 'GDPR-sikker', 'Hostet i EU'],
        problemTitle: 'Det slid, du allerede kender',
        problemBody: 'Fører dit firma regnskab for snesevis af virksomheder, er hverdagen den samme hver måned: PDF’er og scannede bilag tastet ind i hånden, momskoder tjekket ét ad gangen, bilag afstemt mod banken linje for linje. Nødvendigt, korrekt — og det æder dage hver måned.',
        howTitle: 'Sådan virker Accountrix',
        howSteps: [
            { icon: FileText, text: 'Bilag lander i din klients indbakke — vi henter dem derfra' },
            { icon: Cpu, text: 'AI’en læser og koder hvert enkelt mod klientens egen kontoplan (leverandør, beløb og den rigtige danske momskode efter land)' },
            { icon: CheckCircle, text: 'Din bogholder gennemgår og godkender' },
            { icon: GitCompareArrows, text: 'Posteringerne matches mod banken' },
            { icon: FileSpreadsheet, text: 'Posteringerne går tilbage i e-conomic eller Uniconta, hvert bilag nummereret, vedhæftet og i rækkefølge' },
        ],
        howFooter: 'Det langsomme er klaret for jer; vurderingen bliver hos jeres team.',
        inboxTitle: 'En sikker indbakke til hver klient',
        inboxBody: 'Bilag kommer ind ad tre veje: læst direkte fra klientens egen indbakke i e-conomic eller Uniconta, videresendt til en dedikeret mailadresse, eller sendt via et sikkert klientlink — slut med at jage papirer på mail. Uanset hvordan de kommer ind, lander det hele ét organiseret sted, klar til at blive behandlet. Dine klienter får en nem måde at aflevere på; I får styr på det hele.',
        econTitle: 'Virker med Uniconta og e-conomic',
        econBody: 'Accountrix kobler sig på det system, klienten allerede bruger. På e-conomic passer vi til de aftaler, dine klienter allerede har — også de mindste: rene posteringer og nummererede bilag direkte i kassekladden, uden at I behøver opgradere. På Uniconta bogfører vi direkte i den daglige kladde med bilaget vedhæftet posteringen. Vi forbereder bogføringen; systemet forbliver jeres.',
        econHighlight: 'Og det gælder alle jeres Uniconta-klienter. Har virksomheden ikke Integrationspakken, leverer vi præcis det samme arbejde som finanskladde med nummererede bilag. Ingen opgradering, ingen klient I må sige nej til.',
        pricingTitle: 'Enkel, fair pris',
        pricingBody: 'Fra {founding} kr pr. klient/måned for founding partners ({standard} kr standard). Én pris pr. klient — ingen lofter på antal bilag, ingen trin, ingen overraskelser. Det er en brøkdel af den bogholdertid, det sparer på blot én klient hver måned.',
        pricingSmall: 'Founding-partner-pris til vores første firmaer — book en demo, så sætter vi jer op.',
        trustTitle: 'Bygget til tillid med klientdata',
        trustBody: 'Klientdata håndteres efter GDPR — krypteret, adgangsstyret, hostet i EU og aldrig delt. Accountrix er jeres databehandler; I er fortsat dataansvarlig og har altid kontrol over jeres klienters oplysninger.',
        trustItems: [
            { icon: Lock, text: 'Krypteret & adgangsstyret' },
            { icon: Globe, text: 'Hostet i EU' },
            { icon: ShieldCheck, text: 'GDPR — I er dataansvarlig' },
        ],
        closingTitle: 'Se det på jeres egne klienter.',
        closingBody: 'Book en kort demo, så viser vi dit team det hele — helt uforpligtende.',
        footerTagline: 'AI-forberedt bogføring til danske revisorer og bogholdere.',
        footerLinks: [
            { label: 'Privatlivspolitik', to: '/privatlivspolitik' },
            { label: 'Handelsbetingelser', to: '/handelsbetingelser' },
            { label: 'Cookies', to: '/cookies' },
            { label: 'Databehandleraftale (DPA)', to: '/dpa' },
        ],
        footerContact: 'Kontakt',
        rights: 'Alle rettigheder forbeholdes.',
        contactTitle: 'Book en demo',
        contactBody: 'Skriv til os, så vender vi tilbage hurtigst muligt.',
        fName: 'Navn', fCompany: 'Virksomhed', fCvr: 'CVR (valgfrit)', fEmail: 'E-mail',
        fMessage: 'Besked', fSend: 'Send besked', fSending: 'Sender…',
        fOkTitle: 'Tak — vi har modtaget din besked.',
        fOkBody: 'Vi vender tilbage på den e-mail, du har angivet.',
        fPartialTitle: 'Vi har gemt din besked.',
        fPartialBody: 'Vores notifikation kunne ikke sendes lige nu, men beskeden er registreret. Har du hastværk, så skriv direkte til',
        fErrTitle: 'Beskeden blev ikke sendt.',
        fErrBody: 'Prøv igen om lidt, eller skriv direkte til',
        fRequired: 'Udfyld navn, virksomhed, e-mail og besked.',
    },
    en: {
        nav: { how: 'How it works', inbox: 'Client inbox', pricing: 'Pricing', security: 'Security' },
        login: 'Log ind',
        eyebrow: 'AI bookkeeping prep for Danish accounting firms — Uniconta and e-conomic',
        headline: 'Turn days of invoice entry into hours of review.',
        subhead: 'Accountrix reads your clients’ bilag straight from the inbox of their own accounting system, codes them against that client’s chart of accounts and the right Danish VAT codes, matches them to the bank, and posts them back into e-conomic or Uniconta — so your team reviews instead of types. You stay in control; nothing is posted without your approval.',
        btnDemo: 'Book a demo',
        btnHow: 'See how it works',
        trust: ['Works with Uniconta and e-conomic', 'GDPR-compliant', 'Hosted in the EU'],
        problemTitle: 'The grind you already know',
        problemBody: 'If your firm keeps the books for dozens of companies, the monthly reality is the same: PDFs and scanned receipts typed in by hand, VAT codes checked one at a time, receipts reconciled against the bank line by line. Necessary, accurate — and it quietly eats days.',
        howTitle: 'How Accountrix works',
        howSteps: [
            { icon: FileText, text: 'Bilag arrive in your client’s inbox — we read them from there' },
            { icon: Cpu, text: 'The AI reads and codes each one against that client’s own chart of accounts (vendor, amount, and the correct Danish VAT code by country)' },
            { icon: CheckCircle, text: 'Your accountant reviews and approves' },
            { icon: GitCompareArrows, text: 'Entries are matched to the bank' },
            { icon: FileSpreadsheet, text: 'Entries go back into e-conomic or Uniconta, each bilag numbered, attached and in order' },
        ],
        howFooter: 'The slow part is done for you; the judgement stays with your team.',
        inboxTitle: 'A secure inbox for every client',
        inboxBody: 'Bilag reach you three ways: read straight from the client’s own inbox in e-conomic or Uniconta, forwarded to a dedicated email address, or sent through a secure client link — no more chasing paperwork over email. However they arrive, it all lands in one organized place, ready to process. Your clients get a simple way to hand things over; you get everything in order.',
        econTitle: 'Works with Uniconta and e-conomic',
        econBody: 'Accountrix connects to the system your client already uses. On e-conomic we fit the agreements your clients are already on — including entry-level plans: clean entries and numbered bilag straight into the kassekladde, with no upgrade required. On Uniconta we post directly into the daily journal with the bilag attached to the entry. We prepare the bookkeeping; the system stays yours.',
        econHighlight: 'And that covers every one of your Uniconta clients. Where the company doesn’t have the Integrationspakke, we deliver exactly the same work as a finanskladde file with numbered bilag. No upgrade, and no client you have to turn away.',
        pricingTitle: 'Simple, fair pricing',
        pricingBody: 'From {founding} kr per client / month for founding partners ({standard} kr standard). One price per client — no transaction caps, no tiers, no surprises. That’s a fraction of the bookkeeper time it saves on a single client every month.',
        pricingSmall: 'Founding-partner rate for our first firms — book a demo and we’ll set you up.',
        trustTitle: 'Built for trust with client data',
        trustBody: 'Client data is handled to GDPR standards — encrypted, access-controlled, hosted in the EU, never shared. Accountrix acts as your firm’s data processor; you remain the controller, always in control of your clients’ information.',
        trustItems: [
            { icon: Lock, text: 'Encrypted & access-controlled' },
            { icon: Globe, text: 'Hosted in the EU' },
            { icon: ShieldCheck, text: 'GDPR — you stay the controller' },
        ],
        closingTitle: 'See it on your own clients.',
        closingBody: 'Book a short demo and we’ll walk your team through it — no commitment.',
        footerTagline: 'AI bookkeeping prep for Danish accounting firms.',
        footerLinks: [
            { label: 'Privacy Policy', to: '/privatlivspolitik' },
            { label: 'Terms', to: '/handelsbetingelser' },
            { label: 'Cookies', to: '/cookies' },
            { label: 'DPA', to: '/dpa' },
        ],
        footerContact: 'Contact',
        rights: 'All rights reserved.',
        contactTitle: 'Book a demo',
        contactBody: 'Send us a message and we\u2019ll get back to you shortly.',
        fName: 'Name', fCompany: 'Company', fCvr: 'CVR (optional)', fEmail: 'Email',
        fMessage: 'Message', fSend: 'Send message', fSending: 'Sending\u2026',
        fOkTitle: 'Thanks — we\u2019ve received your message.',
        fOkBody: 'We\u2019ll reply to the email address you gave us.',
        fPartialTitle: 'Your message is saved.',
        fPartialBody: 'Our notification could not be sent just now, but your message is recorded. If it is urgent, email us directly at',
        fErrTitle: 'Your message was not sent.',
        fErrBody: 'Please try again shortly, or email us directly at',
        fRequired: 'Please fill in name, company, email and message.',
    },
};

function LangToggle({ lang, setLang }) {
    const base = 'px-2 py-1 text-sm font-medium rounded transition-colors';
    return (
        <div className="flex items-center gap-1" aria-label="Language">
            <button
                onClick={() => setLang('da')}
                className={`${base} ${lang === 'da' ? 'text-[#0F766E]' : 'text-slate-400 hover:text-slate-600'}`}
                aria-pressed={lang === 'da'}
            >DA</button>
            <span className="text-slate-300">|</span>
            <button
                onClick={() => setLang('en')}
                className={`${base} ${lang === 'en' ? 'text-[#0F766E]' : 'text-slate-400 hover:text-slate-600'}`}
                aria-pressed={lang === 'en'}
            >EN</button>
        </div>
    );
}

export default function LandingPage() {
    const [lang, setLang] = useState('da');
    const t = STR[lang];
    // The price comes from the backend (GET /api/pricing -> pricing.py), not from this file.
    // Hardcoding it here made the marketing site a place the number could go stale independently
    // of what the product actually bills. The literals in the copy above are only a fallback for
    // the moment before the fetch resolves.
    const [price, setPrice] = React.useState({ founding: 69, standard: 99 });
    React.useEffect(() => {
        let alive = true;
        fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/pricing`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
                if (alive && d) {
                    setPrice({
                        founding: d.founding_partner_price_per_client,
                        standard: d.standard_price_per_client,
                    });
                }
            })
            .catch(() => {});
        return () => { alive = false; };
    }, []);
    const pricingBody = (t.pricingBody || '')
        .replace('{founding}', price.founding)
        .replace('{standard}', price.standard);

    return (
        <div className="min-h-screen bg-white text-slate-900 antialiased">
            {/* 1. Header */}
            <header className="sticky top-0 z-50 border-b border-slate-100 bg-white/90 backdrop-blur-sm">
                <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
                    <a href="#top" className="font-heading text-xl font-bold tracking-tight text-[#0F766E]">
                        Accountrix
                    </a>
                    <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
                        <a href="#how" className="hover:text-slate-900 transition-colors">{t.nav.how}</a>
                        <a href="#inbox" className="hover:text-slate-900 transition-colors">{t.nav.inbox}</a>
                        <a href="#pricing" className="hover:text-slate-900 transition-colors">{t.nav.pricing}</a>
                        <a href="#security" className="hover:text-slate-900 transition-colors">{t.nav.security}</a>
                    </nav>
                    <div className="flex items-center gap-3">
                        <LangToggle lang={lang} setLang={setLang} />
                        <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
                            {t.login}
                        </Link>
                        <a href="#contact" className="hidden sm:inline-flex">
                            <Button className="bg-[#0F766E] hover:bg-[#0c5d56] text-white">{t.btnDemo}</Button>
                        </a>
                    </div>
                </div>
            </header>

            {/* 2. Hero */}
            <section id="top" className="relative overflow-hidden">
                <div className="max-w-6xl mx-auto px-6 pt-20 pb-16 md:pt-28 md:pb-24">
                    <div className="max-w-3xl">
                        <p className="text-sm font-semibold uppercase tracking-wide text-[#0F766E] mb-5">
                            {t.eyebrow}
                        </p>
                        <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tight leading-[1.08] text-slate-900">
                            {t.headline}
                        </h1>
                        <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed">
                            {t.subhead}
                        </p>
                        <div className="mt-9 flex flex-col sm:flex-row gap-3">
                            <a href="#contact" className="w-full sm:w-auto">
                                <Button size="lg" className="w-full sm:w-auto bg-[#0F766E] hover:bg-[#0c5d56] text-white px-8">
                                    {t.btnDemo}
                                </Button>
                            </a>
                            <a href="#how" className="w-full sm:w-auto">
                                <Button size="lg" variant="outline" className="w-full sm:w-auto border-slate-300 text-slate-700 hover:bg-slate-50 px-8">
                                    {t.btnHow} <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </a>
                        </div>
                        {/* Trust strip — truthful signals only */}
                        <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
                            {t.trust.map((item, i) => (
                                <span key={i} className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-[#0F766E]" />
                                    {item}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Light hero graphic: invoice → AI → e-conomic */}
                    <div className="mt-16 hidden md:flex items-center justify-center gap-6">
                        {[
                            { icon: FileText, label: lang === 'da' ? 'Bilag' : 'Invoice' },
                            { icon: Cpu, label: 'AI' },
                            { icon: FileSpreadsheet, label: 'e-conomic' },
                        ].map((node, i, arr) => (
                            <React.Fragment key={i}>
                                <div className="flex flex-col items-center gap-3">
                                    <div className="w-20 h-20 rounded-2xl bg-[#0F766E]/10 border border-[#0F766E]/20 flex items-center justify-center">
                                        <node.icon className="w-9 h-9 text-[#0F766E]" />
                                    </div>
                                    <span className="text-sm font-medium text-slate-500">{node.label}</span>
                                </div>
                                {i < arr.length - 1 && <ArrowRight className="w-6 h-6 text-slate-300 mb-7" />}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </section>

            {/* 3. Problem */}
            <section className="bg-slate-50 border-y border-slate-100">
                <div className="max-w-3xl mx-auto px-6 py-20 md:py-24">
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                        {t.problemTitle}
                    </h2>
                    <p className="mt-6 text-lg text-slate-600 leading-relaxed">{t.problemBody}</p>
                </div>
            </section>

            {/* 4. How it works */}
            <section id="how" className="max-w-5xl mx-auto px-6 py-20 md:py-28">
                <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900 text-center">
                    {t.howTitle}
                </h2>
                <ol className="mt-12 grid gap-5 md:grid-cols-5">
                    {t.howSteps.map((step, i) => (
                        <li key={i} className="relative flex md:flex-col items-start md:items-center gap-4 md:gap-3 text-left md:text-center">
                            <div className="shrink-0 w-12 h-12 rounded-xl bg-[#0F766E]/10 border border-[#0F766E]/20 flex items-center justify-center">
                                <step.icon className="w-6 h-6 text-[#0F766E]" />
                            </div>
                            <p className="text-sm text-slate-600 leading-relaxed">
                                <span className="block font-semibold text-[#0F766E] mb-1">{i + 1}</span>
                                {step.text}
                            </p>
                        </li>
                    ))}
                </ol>
                <p className="mt-12 text-center text-lg font-medium text-slate-800 max-w-2xl mx-auto">
                    {t.howFooter}
                </p>
            </section>

            {/* 5. Secure client inbox */}
            <section id="inbox" className="bg-slate-50 border-y border-slate-100">
                <div className="max-w-5xl mx-auto px-6 py-20 md:py-24 grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                            {t.inboxTitle}
                        </h2>
                        <p className="mt-6 text-lg text-slate-600 leading-relaxed">{t.inboxBody}</p>
                    </div>
                    <div className="flex justify-center">
                        <div className="w-28 h-28 rounded-3xl bg-[#0F766E]/10 border border-[#0F766E]/20 flex items-center justify-center">
                            <Inbox className="w-14 h-14 text-[#0F766E]" />
                        </div>
                    </div>
                </div>
            </section>

            {/* 6. Works with e-conomic */}
            <section className="max-w-5xl mx-auto px-6 py-20 md:py-24 grid md:grid-cols-2 gap-12 items-center">
                <div className="flex justify-center md:order-last">
                    <div className="w-28 h-28 rounded-3xl bg-[#0F766E]/10 border border-[#0F766E]/20 flex items-center justify-center">
                        <FileSpreadsheet className="w-14 h-14 text-[#0F766E]" />
                    </div>
                </div>
                <div>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                        {t.econTitle}
                    </h2>
                    <p className="mt-6 text-lg text-slate-600 leading-relaxed">{t.econBody}</p>
                    {/* The reach line gets its own emphasised block: it is the strongest commercial
                        sentence on the page — every Uniconta client is reachable, licensed or not. */}
                    <p className="mt-6 text-lg font-medium text-slate-900 leading-relaxed border-l-4 border-emerald-500 pl-5">
                        {t.econHighlight}
                    </p>
                </div>
            </section>

            {/* 7. Pricing — text framing, no bare badge */}
            <section id="pricing" className="bg-slate-50 border-y border-slate-100">
                <div className="max-w-3xl mx-auto px-6 py-20 md:py-24 text-center">
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                        {t.pricingTitle}
                    </h2>
                    <p className="mt-6 text-lg text-slate-600 leading-relaxed">{pricingBody}</p>
                    <p className="mt-5 text-sm text-slate-500">{t.pricingSmall}</p>
                    <div className="mt-8">
                        <a href="#contact">
                            <Button size="lg" className="bg-[#0F766E] hover:bg-[#0c5d56] text-white px-8">{t.btnDemo}</Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* 8. Trust & security */}
            <section id="security" className="max-w-5xl mx-auto px-6 py-20 md:py-24">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                        {t.trustTitle}
                    </h2>
                    <p className="mt-6 text-lg text-slate-600 leading-relaxed">{t.trustBody}</p>
                </div>
                <div className="mt-12 grid gap-5 sm:grid-cols-3 max-w-3xl mx-auto">
                    {t.trustItems.map((item, i) => (
                        <div key={i} className="flex flex-col items-center text-center gap-3 rounded-2xl border border-slate-100 bg-white p-6">
                            <item.icon className="w-7 h-7 text-[#0F766E]" />
                            <span className="text-sm font-medium text-slate-700">{item.text}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* 8b. Contact form — replaces the old Book-a-demo mailto. */}
            <section id="contact" className="max-w-3xl mx-auto px-6 py-20 md:py-24">
                <div className="text-center">
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                        {t.contactTitle}
                    </h2>
                    <p className="mt-5 text-lg text-slate-600 leading-relaxed">{t.contactBody}</p>
                </div>
                <div className="mt-10">
                    <ContactForm t={t} />
                </div>
            </section>

            {/* 9. Closing CTA */}
            <section className="bg-[#0F766E]">
                <div className="max-w-3xl mx-auto px-6 py-20 md:py-24 text-center">
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-white">
                        {t.closingTitle}
                    </h2>
                    <p className="mt-5 text-lg text-teal-50/90 leading-relaxed">{t.closingBody}</p>
                    <div className="mt-9">
                        <a href="#contact">
                            <Button size="lg" className="bg-white text-[#0F766E] hover:bg-teal-50 px-8">{t.btnDemo}</Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* 10. Footer */}
            <footer className="bg-slate-900 text-slate-300">
                <div className="max-w-6xl mx-auto px-6 py-14">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-10">
                        <div className="max-w-xs">
                            <span className="font-heading text-xl font-bold text-white">Accountrix</span>
                            <p className="mt-3 text-sm text-slate-400 leading-relaxed">{t.footerTagline}</p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-10">
                            <div>
                                <ul className="space-y-2 text-sm">
                                    {t.footerLinks.map((l) => (
                                        <li key={l.to}>
                                            <Link to={l.to} className="text-slate-400 hover:text-white transition-colors">
                                                {l.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-white mb-2">{t.footerContact}</p>
                                <a href={`mailto:${DEMO_EMAIL}`} className="text-sm text-slate-400 hover:text-white transition-colors">
                                    {DEMO_EMAIL}
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
                        <span>© {new Date().getFullYear()} Accountrix. {t.rights}</span>
                        <span className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> {t.trust[0]} · {t.trust[1]} · {t.trust[2]}</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
